import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { logWarn } from "@/lib/log";

const PREVIEW_SEC = 30;

/**
 * `useAudio` — owns the HTMLAudioElement, its event listeners, and the
 * preview-cap gating logic.
 *
 * Preview window model:
 *   Non-owners can only hear a 30-second slice of a paid track, starting at
 *   `access.preview_start_sec` (artist-chosen — defaults to 0). We seek to
 *   that offset on load, and cap playback at `preview_start_sec + 30`.
 */
export function useAudio({ current, playing, setPlaying, access, onEnded }) {
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const accessRef = useRef(access);
  const currentRef = useRef(current);
  useEffect(() => { accessRef.current = access; }, [access]);
  useEffect(() => { currentRef.current = current; }, [current]);

  // Preview window helpers — always read the LATEST access via the ref
  // so long-lived listeners never see a stale value.
  const previewStart = () => Number(accessRef.current?.preview_start_sec) || 0;
  const previewEnd = () => previewStart() + PREVIEW_SEC;

  // Attach the audio element + listeners exactly once for the lifetime of the app.
  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    audioRef.current = a;

    const onTime = () => {
      setProgress(a.currentTime);
      if (!accessRef.current.full && a.currentTime >= previewEnd()) {
        a.pause();
        setPlaying(false);
        toast("Preview ended — buy the track to hear the full song", {
          action: {
            label: "Buy $0.99",
            onClick: () => {
              const c = currentRef.current;
              if (c?.id) window.location.href = `/album/${c.album_id || ""}`;
            },
          },
        });
      }
    };
    const onMeta = () => setDuration(a.duration || 0);
    const onEndedListener = () => onEnded();

    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEndedListener);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEndedListener);
      a.pause();
    };
    // Intentionally single-run: listeners reference refs for freshness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback((src) => {
    if (!audioRef.current) return;
    audioRef.current.src = src;
    if (playing) {
      audioRef.current.play().catch((err) => {
        logWarn("[player] play() rejected", err?.name);
        setPlaying(false);
      });
    }
  }, [playing, setPlaying]);

  // Seek to the artist-picked hook offset whenever access resolves for a
  // preview-mode listener. Runs AFTER the /access GET completes (not during
  // load()) so the seek uses fresh accessRef data, not the DEFAULT_ACCESS.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || access.full) return;
    const start = Number(access.preview_start_sec) || 0;
    if (start <= 0) return;
    const jump = () => {
      try { a.currentTime = start; }
      catch (e) { logWarn("[player] seek to preview start failed", e?.message); }
    };
    // If metadata already loaded, seek now; otherwise wait for it.
    if (a.readyState >= 1) jump();
    else {
      a.addEventListener("loadedmetadata", jump, { once: true });
      return () => a.removeEventListener("loadedmetadata", jump);
    }
  }, [access.full, access.preview_start_sec, current?.id]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    // If preview ran out, rewind to the artist's picked start, not to 0.
    if (!accessRef.current.full && a.currentTime >= previewEnd() - 0.5) {
      a.currentTime = previewStart();
    }
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  }, [setPlaying]);

  const seek = useCallback((t) => {
    if (!audioRef.current) return;
    if (accessRef.current?.full) {
      audioRef.current.currentTime = t;
      return;
    }
    // Non-owners: clamp seeks to the preview window [start, start+30].
    const start = previewStart();
    audioRef.current.currentTime = Math.max(start, Math.min(t, start + PREVIEW_SEC - 0.2));
  }, []);

  return { progress, duration, load, toggle, seek, PREVIEW_SEC };
}
